/**
 * Gift Premium Page — create multi-use gift codes with quantity, view history.
 * Flow: select quantity + plan + provider → payment → promo code generated.
 */

import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Gift, Copy, Share2, Info, ChevronRight, Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMyGifts, usePricing, useCreateGift } from '@turnifly/shared/hooks'
import { useAuthStore } from '@turnifly/shared/stores'
import { Button } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { ProviderSelector } from './provider-selector'
import { format } from 'date-fns'
import type { GiftSubscriptionItem } from '@turnifly/shared/api'

interface GiftPremiumPageProps {
  isOpen: boolean
  onClose: () => void
}

export const GiftPremiumPage: React.FC<GiftPremiumPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: gifts, isLoading } = useMyGifts(isOpen ? user?.id : undefined)
  const { data: pricingList } = usePricing()
  const createGiftMutation = useCreateGift()

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly')
  const [quantity, setQuantity] = useState('1')
  const [message, setMessage] = useState('')
  const [selectedProvider, setSelectedProvider] = useState<'payme' | 'click'>('payme')
  const [showHowItWorks, setShowHowItWorks] = useState(false)

  // Dynamic pricing from DB
  const monthlyPrice = useMemo(
    () => pricingList?.find((p) => p.plan === 'premium' && p.period === 'monthly' && p.currency === 'UZS')?.amount ?? 15000,
    [pricingList],
  )
  const yearlyPrice = useMemo(
    () => pricingList?.find((p) => p.plan === 'premium' && p.period === 'yearly' && p.currency === 'UZS')?.amount ?? 99000,
    [pricingList],
  )

  const unitPrice = selectedPlan === 'monthly' ? monthlyPrice : yearlyPrice
  const qty = Math.max(1, Math.min(100, parseInt(quantity, 10) || 1))
  const totalAmount = unitPrice * qty

  const formatPrice = (amount: number): string => amount.toLocaleString('uz-UZ')

  const handleCreate = (): void => {
    if (qty < 1) return
    hapticImpact('medium')

    createGiftMutation.mutate(
      {
        plan: selectedPlan,
        quantity: qty,
        message: message.trim() || undefined,
        provider: selectedProvider,
      },
      {
        onSuccess: (result) => {
          if (result.checkout_url) {
            if (result.payment_id) {
              localStorage.setItem('last_payment_id', result.payment_id)
            }
            window.open(result.checkout_url, '_blank')
          }
          hapticNotification('success')
          setQuantity('1')
          setMessage('')
        },
        onError: () => {
          hapticNotification('error')
        },
      },
    )
  }

  const handleCopy = async (code: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code)
      hapticNotification('success')
    } catch {
      const ta = document.createElement('textarea')
      ta.value = code
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      hapticNotification('success')
    }
  }

  const handleShareCode = (code: string): void => {
    const shareText = `Turnifly Premium sovg'a! Promokod: ${code}`
    const url = `https://t.me/share/url?url=${encodeURIComponent('https://t.me/turnifly_bot')}&text=${encodeURIComponent(shareText)}`
    window.open(url, '_blank', 'noopener')
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md
              lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            {/* Header — safe area */}
            <div className="flex items-center justify-between px-5 pb-2 shrink-0" style={{ paddingTop: 'max(20px, calc(var(--tg-safe-area-inset-top) + 8px))' }}>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04]">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('giftPremium.title')}</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5" style={{ paddingBottom: 'max(32px, calc(var(--tg-safe-area-inset-bottom) + 16px))' }}>
              {/* How it works */}
              <button
                onClick={() => { setShowHowItWorks(!showHowItWorks); hapticImpact('light') }}
                className="w-full flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-primary/10 mb-4"
              >
                <Info size={16} className="text-primary shrink-0" />
                <span className="flex-1 text-sm text-text text-left">{t('giftPremium.howItWorks')}</span>
                <ChevronRight size={16} className={`text-primary transition-transform ${showHowItWorks ? 'rotate-90' : ''}`} />
              </button>

              <AnimatePresence>
                {showHowItWorks && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mb-4"
                  >
                    <div className="space-y-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                      {[1, 2, 3].map((step) => (
                        <div key={step} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-[10px] font-bold text-primary">{step}</span>
                          </div>
                          <p className="text-sm text-text/80">{t(`giftPremium.step${step}`)}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Create Gift Form */}
              <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 space-y-4">
                {/* Quantity */}
                <div>
                  <label className="text-xs font-medium text-text-hint mb-1.5 block">
                    <Users size={12} className="inline mr-1" />
                    {t('giftPremium.quantity')}
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 3)
                      setQuantity(val || '')
                    }}
                    onBlur={() => {
                      const n = parseInt(quantity, 10)
                      if (!n || n < 1) setQuantity('1')
                      else if (n > 100) setQuantity('100')
                    }}
                    placeholder={t('giftPremium.quantityPlaceholder')}
                    className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06]
                      text-text text-base font-mono placeholder:text-text-hint/40
                      focus:outline-none focus:border-primary/30"
                  />
                </div>

                {/* Plan toggle */}
                <div>
                  <label className="text-xs font-medium text-text-hint mb-1.5 block">
                    {t('giftPremium.plan')}
                  </label>
                  <div className="flex gap-2">
                    {(['monthly', 'yearly'] as const).map((plan) => (
                      <button
                        key={plan}
                        onClick={() => { setSelectedPlan(plan); hapticImpact('light') }}
                        className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                          selectedPlan === plan
                            ? 'bg-primary/15 text-primary border border-primary/20'
                            : 'bg-white/[0.04] text-text-hint border border-transparent hover:bg-white/[0.06]'
                        }`}
                      >
                        {plan === 'monthly' ? t('giftPremium.monthly') : t('giftPremium.yearly')}
                        <span className="block text-[10px] mt-0.5 opacity-70">
                          {formatPrice(plan === 'monthly' ? monthlyPrice : yearlyPrice)} UZS
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="text-xs font-medium text-text-hint mb-1.5 block">
                    {t('giftPremium.message')}
                  </label>
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 200))}
                    placeholder={t('giftPremium.messagePlaceholder')}
                    className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06]
                      text-text text-sm placeholder:text-text-hint/40
                      focus:outline-none focus:border-primary/30"
                  />
                </div>

                {/* Provider */}
                <ProviderSelector
                  selected={selectedProvider}
                  onChange={setSelectedProvider}
                />

                {/* Total */}
                <div className="flex items-center justify-between pt-2 border-t border-white/[0.04]">
                  <span className="text-sm text-text-hint">{t('giftPremium.total')}</span>
                  <span className="text-xl font-display font-bold text-text">
                    {formatPrice(totalAmount)}
                    <span className="text-sm font-normal text-text-hint ml-1">UZS</span>
                  </span>
                </div>

                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={handleCreate}
                  isLoading={createGiftMutation.isPending}
                  disabled={qty < 1}
                >
                  <Gift size={18} className="mr-2" />
                  {t('giftPremium.create')}
                </Button>
              </div>

              {/* Gift History */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-text mb-3">{t('giftPremium.history')}</h3>

                {isLoading ? (
                  <div className="flex justify-center py-6"><LoadingSpinner size="sm" /></div>
                ) : !gifts?.length ? (
                  <EmptyState
                    icon={<Gift size={36} className="text-text-hint/30" />}
                    title={t('giftPremium.noGifts')}
                    description={t('giftPremium.noGiftsDesc')}
                  />
                ) : (
                  <div className="space-y-2.5">
                    {gifts.map((gift) => (
                      <GiftCard
                        key={gift.id}
                        gift={gift}
                        onCopy={handleCopy}
                        onShare={handleShareCode}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ── Gift Card ───────────────────────────────────────────────

const STATUS_BADGE: Record<string, 'primary' | 'success' | 'danger' | 'warning' | 'outline'> = {
  pending_payment: 'warning',
  created: 'primary',
  redeemed: 'success',
  expired: 'danger',
  cancelled: 'outline',
}

interface GiftCardProps {
  gift: GiftSubscriptionItem
  onCopy: (code: string) => Promise<void>
  onShare: (code: string) => void
}

const GiftCard: React.FC<GiftCardProps> = React.memo(({ gift, onCopy, onShare }) => {
  const { t } = useTranslation()

  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-sm text-primary font-medium">{gift.promo_code}</span>
        <Badge variant={STATUS_BADGE[gift.status] ?? 'primary'} className="text-[9px]">
          {t(`giftPremium.status.${gift.status}`, gift.status)}
        </Badge>
      </div>

      <div className="flex items-center justify-between text-xs text-text-hint mb-3">
        <span>{gift.plan === 'monthly' ? t('giftPremium.monthly') : t('giftPremium.yearly')}</span>
        <span>
          {gift.usage_count}/{gift.usage_limit} {t('giftPremium.used')}
          {gift.usage_limit - gift.usage_count > 0 && gift.status === 'created' && (
            <span className="text-success ml-1">
              · {gift.usage_limit - gift.usage_count} {t('giftPremium.remaining')}
            </span>
          )}
        </span>
      </div>

      {gift.message && (
        <p className="text-xs text-text/60 italic mb-3">"{gift.message}"</p>
      )}

      <div className="flex items-center gap-2 text-xs text-text-hint">
        <span>{format(new Date(gift.created_at), 'dd.MM.yyyy')}</span>
        <div className="flex-1" />
        {gift.status === 'created' && (
          <>
            <button
              onClick={() => void onCopy(gift.promo_code)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.06] transition-colors"
            >
              <Copy size={12} />
              {t('giftPremium.copyCode')}
            </button>
            <button
              onClick={() => onShare(gift.promo_code)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
            >
              <Share2 size={12} />
              {t('giftPremium.shareCode')}
            </button>
          </>
        )}
      </div>
    </div>
  )
})
GiftCard.displayName = 'GiftCard'
